import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { 
  ArrowLeft, Box, Pill, FileText, ShieldAlert, Scissors, 
  MapPin, Activity, Copy, Edit
} from 'lucide-react';

interface PreferenceCard {
  preference_card_id: number;
  org_id: string;
  provider_id: number;
  procedure_name: string;
  procedure_cpt_code: string;
  procedure_category: string;
  equipment_list: any[];
  supplies_list: any[];
  medications_list: any[];
  patient_position: string;
  positioning_devices: string[];
  table_setup_notes: string;
  preferred_anesthesia_type: string;
  special_anesthesia_requirements: string;
  suture_preferences: any[];
  special_instructions: string;
  critical_steps: string[];
  common_complications_to_watch: string[];
  estimated_duration_minutes: number;
  turnover_requirements: string;
  preferred_surgical_tech: string;
  preferred_circulating_nurse: string;
  minimum_staff_required: number;
  last_used_date: string;
  times_used: number;
  version_number: number;
  is_current_version: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function PreferenceCardDetailRoute() {
  const { cardId } = useParams<{ cardId: string }>();
  const navigate = useNavigate();

  const { data: card, isLoading } = useQuery({
    queryKey: ['preference-card-detail', cardId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('surgeon_preference_cards')
        .select('*')
        .eq('preference_card_id', cardId)
        .single();

      if (error) throw error;
      return data as PreferenceCard;
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-full bg-pt-background flex items-center justify-center">
        <div className="text-center">
          <div className="pt-spinner mx-auto mb-4" />
          <p className="text-pt-text-muted">Loading preference card...</p>
        </div>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="min-h-full bg-pt-background flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-16 h-16 text-pt-text-dim mx-auto mb-4" />
          <p className="text-pt-text-muted">Preference card not found</p>
          <button
            onClick={() => navigate('/preference-cards')}
            className="mt-4 text-pt-gold hover:underline"
          >
            Back to all cards
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-pt-background">
      {/* Header */}
      <header className="border-b border-pt-border/60">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/preference-cards')}
                className="text-pt-text-muted hover:text-pt-gold transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-display font-semibold">
                  {card.procedure_name}
                </h1>
                <div className="flex items-center gap-4 mt-1 text-sm text-pt-text-muted">
                  <span className="uppercase tracking-wide">{card.procedure_category}</span>
                  <span>•</span>
                  <span>CPT {card.procedure_cpt_code}</span>
                  <span>•</span>
                  <span>Provider #{card.provider_id}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/preference-cards/new?clone=${card.preference_card_id}`)}
                className="flex items-center gap-2 bg-pt-dark border border-pt-input-border text-pt-text px-4 py-2 rounded-lg hover:border-pt-gold transition-colors"
              >
                <Copy className="w-4 h-4" />
                Clone
              </button>
              <button
                onClick={() => navigate(`/preference-cards/${card.preference_card_id}/edit`)}
                className="flex items-center gap-2 bg-pt-gold text-black px-4 py-2 rounded-lg hover:bg-pt-gold/90 transition-colors font-medium"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Stats & Setup */}
          <div className="space-y-6">
            {/* Stats Card */}
            <div className="bg-pt-panel border border-pt-border rounded-lg p-5">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-pt-text-muted mb-4">
                Usage Statistics
              </h2>
              <div className="space-y-4">
                <div>
                  <div className="text-3xl font-bold text-pt-gold">{card.times_used}</div>
                  <div className="text-sm text-pt-text-muted">Times Used</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-pt-cyan">
                    {card.estimated_duration_minutes}
                  </div>
                  <div className="text-sm text-pt-text-muted">Minutes (avg)</div>
                </div>
                <div>
                  <div className="text-sm text-pt-text-muted">Last Used</div>
                  <div className="text-base font-medium">
                    {card.last_used_date
                      ? new Date(card.last_used_date).toLocaleDateString()
                      : 'Never'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-pt-text-muted">Version</div>
                  <div className="text-base font-medium">v{card.version_number}</div>
                </div>
              </div>
            </div>

            {/* Setup Info */}
            <div className="bg-pt-panel border border-pt-border rounded-lg p-5">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-pt-text-muted mb-4">
                Setup Information
              </h2>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-pt-gold mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-pt-text-muted">Patient Position</div>
                    <div className="text-sm font-medium">{card.patient_position}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Activity className="w-4 h-4 text-pt-cyan mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-pt-text-muted">Anesthesia Type</div>
                    <div className="text-sm font-medium">{card.preferred_anesthesia_type}</div>
                  </div>
                </div>
                {card.minimum_staff_required && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-pt-purple mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-pt-text-muted">Minimum Staff</div>
                      <div className="text-sm font-medium">{card.minimum_staff_required}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Positioning Devices */}
            {card.positioning_devices && card.positioning_devices.length > 0 && (
              <div className="bg-pt-panel border border-pt-border rounded-lg p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-pt-text-muted mb-3">
                  Positioning Devices
                </h2>
                <ul className="space-y-2">
                  {card.positioning_devices.map((device, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-pt-gold" />
                      {device}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right Column - Items & Instructions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Equipment */}
            {card.equipment_list && card.equipment_list.length > 0 && (
              <div className="bg-pt-panel border border-pt-border rounded-lg p-5">
                <div className="flex items-center gap-3 mb-4">
                  <Box className="w-5 h-5 text-pt-gold" />
                  <h2 className="text-lg font-semibold">Equipment</h2>
                  <span className="text-sm text-pt-text-muted">({card.equipment_list.length})</span>
                </div>
                <ul className="space-y-2">
                  {card.equipment_list.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm py-2 border-b border-pt-border last:border-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-pt-gold mt-2 flex-shrink-0" />
                      <span>{typeof item === 'string' ? item : JSON.stringify(item)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Supplies */}
            {card.supplies_list && card.supplies_list.length > 0 && (
              <div className="bg-pt-panel border border-pt-border rounded-lg p-5">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="w-5 h-5 text-pt-cyan" />
                  <h2 className="text-lg font-semibold">Supplies</h2>
                  <span className="text-sm text-pt-text-muted">({card.supplies_list.length})</span>
                </div>
                <ul className="space-y-2">
                  {card.supplies_list.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm py-2 border-b border-pt-border last:border-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-pt-cyan mt-2 flex-shrink-0" />
                      <span>{typeof item === 'string' ? item : JSON.stringify(item)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Medications */}
            {card.medications_list && card.medications_list.length > 0 && (
              <div className="bg-pt-panel border border-pt-border rounded-lg p-5">
                <div className="flex items-center gap-3 mb-4">
                  <Pill className="w-5 h-5 text-pt-purple" />
                  <h2 className="text-lg font-semibold">Medications</h2>
                  <span className="text-sm text-pt-text-muted">({card.medications_list.length})</span>
                </div>
                <ul className="space-y-2">
                  {card.medications_list.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm py-2 border-b border-pt-border last:border-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-pt-purple mt-2 flex-shrink-0" />
                      <span>{typeof item === 'string' ? item : JSON.stringify(item)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Suture Preferences */}
            {card.suture_preferences && card.suture_preferences.length > 0 && (
              <div className="bg-pt-panel border border-pt-border rounded-lg p-5">
                <div className="flex items-center gap-3 mb-4">
                  <Scissors className="w-5 h-5 text-pt-green" />
                  <h2 className="text-lg font-semibold">Suture Preferences</h2>
                </div>
                <div className="space-y-3">
                  {card.suture_preferences.map((suture: any, idx) => (
                    <div key={idx} className="bg-pt-dark border border-pt-input-border rounded-lg p-3">
                      <div className="grid grid-cols-4 gap-3 text-sm">
                        <div>
                          <div className="text-xs text-pt-text-muted">Type</div>
                          <div className="font-medium">{suture.type}</div>
                        </div>
                        <div>
                          <div className="text-xs text-pt-text-muted">Size</div>
                          <div className="font-medium">{suture.size}</div>
                        </div>
                        <div>
                          <div className="text-xs text-pt-text-muted">Material</div>
                          <div className="font-medium">{suture.material}</div>
                        </div>
                        <div>
                          <div className="text-xs text-pt-text-muted">Needle</div>
                          <div className="font-medium">{suture.needle}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Special Instructions */}
            {card.special_instructions && (
              <div className="bg-pt-panel border border-pt-border rounded-lg p-5">
                <div className="flex items-center gap-3 mb-4">
                  <ShieldAlert className="w-5 h-5 text-pt-gold" />
                  <h2 className="text-lg font-semibold">Special Instructions</h2>
                </div>
                <p className="text-sm text-pt-text-muted leading-relaxed whitespace-pre-wrap">
                  {card.special_instructions}
                </p>
              </div>
            )}

            {/* Table Setup Notes */}
            {card.table_setup_notes && (
              <div className="bg-pt-panel border border-pt-border rounded-lg p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-pt-text-muted mb-3">
                  Table Setup Notes
                </h2>
                <p className="text-sm text-pt-text-muted leading-relaxed whitespace-pre-wrap">
                  {card.table_setup_notes}
                </p>
              </div>
            )}

            {/* Critical Steps */}
            {card.critical_steps && card.critical_steps.length > 0 && (
              <div className="bg-pt-panel border border-pt-border rounded-lg p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-pt-text-muted mb-3">
                  Critical Steps
                </h2>
                <ol className="space-y-2">
                  {card.critical_steps.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm">
                      <span className="text-pt-gold font-bold">{idx + 1}.</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Complications to Watch */}
            {card.common_complications_to_watch && card.common_complications_to_watch.length > 0 && (
              <div className="bg-pt-panel border border-pt-border rounded-lg p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-pt-text-muted mb-3">
                  Common Complications to Watch
                </h2>
                <ul className="space-y-2">
                  {card.common_complications_to_watch.map((complication, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 flex-shrink-0" />
                      <span>{complication}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
